import { useEffect, useState } from "react";
import { SESSION_EXPIRED_EVENT } from "../CustomerApiService";
import { CustomerContext, useCustomerContext } from "./useCustomerContext";

/**
 * The one thing every interface header/screen renders through to decide whether it's safe to
 * reveal anything identifying (logo, product wordmark, nav tab labels, org name — see
 * `SpotterHeader.tsx`/`AbHeader.tsx`). "pending" and "denied" both render the SAME placeholder
 * chrome; "denied" additionally shows `InterfaceAccessNotice` in place of real content.
 *
 * `denied`'s two reasons get different copy (`InterfaceAccessNotice`) but identical treatment
 * everywhere else — neither one ever reveals which interface was being requested.
 */
export type InterfaceAccessState =
  | { status: "pending" }
  | { status: "granted"; customerName: string }
  | { status: "denied"; reason: "unauthenticated" | "not-enabled" };

interface ContextQueryState {
  data: CustomerContext | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Pure branching behind `useInterfaceAccess`, split out so it's unit-testable without a
 * `QueryClient`/rendered component (see `apps/customer-frontend/CLAUDE.md`'s convention of
 * exporting the decision as a plain function rather than only a hook).
 *
 * `interfaceId: null` is for call sites with no specific interface to check (the index route —
 * there's nothing there to grant access TO), so it skips the `enabledInterfaces` lookup and is
 * "not-enabled" whenever authenticated. Same "unauthenticated"/"pending" branches either way.
 */
export function deriveInterfaceAccess(
  interfaceId: string | null,
  query: ContextQueryState
): InterfaceAccessState {
  if (query.isError) {
    return { status: "denied", reason: "unauthenticated" };
  }
  if (query.isLoading || !query.data) {
    return { status: "pending" };
  }
  const enabled =
    interfaceId !== null &&
    query.data.enabledInterfaces.some((i) => i.id === interfaceId);
  if (!enabled) {
    return { status: "denied", reason: "not-enabled" };
  }
  return { status: "granted", customerName: query.data.customerName };
}

/**
 * Listens for `SESSION_EXPIRED_EVENT` (`CustomerApiService.handleResponse` dispatches it on
 * EVERY 401, from any request, not just `/context`'s own) — this is what makes a mid-session
 * expiry noticed by a screen whose *own* queries 401 even though the already-cached
 * `customer-context` query has nothing new to report. Local `useState`, not react-query: this is
 * a one-way "the session is dead" flag, not server data to cache/refetch.
 */
function useSessionExpired(): boolean {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    if (expired) {
      return;
    }
    const onExpired = () => setExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [expired]);
  return expired;
}

/**
 * `interfaceId` must match the `requirePlugin(interfaceId)` string `apps/customer-api` gates that
 * interface's routes with (`routes/index.ts`) — no shared package to import it from, so each
 * feature's `config.ts` re-declares its own constant with a doc comment pointing back here (same
 * pattern `accessError.ts` already uses for `SPOTTER_ACCESS_DENIED_CODE`).
 *
 * A session dying mid-use (`useSessionExpired` above) is folded straight into `isError` — from
 * every mounted interface's point of view it's indistinguishable from the `customer-context`
 * query itself 401ing: same "denied: unauthenticated" branch, same placeholder chrome +
 * `InterfaceAccessNotice`, no full-page swap.
 */
export function useInterfaceAccess(
  interfaceId: string | null
): InterfaceAccessState {
  const { data, isLoading, isError } = useCustomerContext();
  const sessionExpired = useSessionExpired();
  return deriveInterfaceAccess(interfaceId, {
    data,
    isLoading,
    isError: isError || sessionExpired,
  });
}
